import React, {useEffect, useState} from 'react';

import {Alert, Button} from '@betareader/design-system';
import Icon from '../../../../components/AppIcon';
import styled from 'styled-components';
import countWords from 'word-character-count';
import {Trans, useTranslation} from 'react-i18next';

import ChapterParser from './chapterParser';
import ux from '../../../../utils/uxAnalyticsUtil';
import sanitizeHtml from '../../../../utils/sanitizeHtmlUtil';
import transformCalloutsForPreview from './calloutPreviewHtml';

import './post-processor.css';
import usePostProcessingContext from './postProcessingContext';
import {
  ChapterSidebar,
  SidebarToggle
} from '../../../../components/chapterSidebar';

const ContentBlock = styled.div`
  background-color: var(--br-surface);
  padding: 1em;
  > p:not(:last-child):hover {
    padding-bottom: 10px;
    border-bottom: 2px dashed black;
    cursor: pointer;
  }
`;

const PreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: var(--br-surface);
  color: var(--br-text);
`;

const PreviewSidebar = styled(ChapterSidebar)`
  background: var(--br-raised-surface);
  color: var(--br-text);
  border-right: 1px solid var(--br-border);
  li {
    margin: 8px 0;
  }
  li > button, select {
    width: 100%;
    box-sizing: border-box;
    padding: 8px;
    border: 1px solid var(--br-border);
    border-radius: 6px;
    background: var(--br-surface);
    color: var(--br-text);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
`;

const PreviewContent = styled.main`
  box-sizing: border-box;
  max-width: 768px;
  height: 100vh;
  margin: 0 auto;
  padding: 64px 16px 32px;
  overflow-y: auto;
`;

const PreviewActions = styled.div`
  position: fixed;
  right: 16px;
  top: 12px;
  z-index: 3;
  display: flex;
  gap: 8px;
`;

const TitleInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  padding: 8px 0;
  border: 0;
  border-bottom: 1px solid var(--br-border);
  background: transparent;
  color: var(--br-text);
  font: inherit;
  font-size: 1.8rem;
  font-weight: bold;
`;

const ChapterFilePostProcessor = ({
  onCompleteClicked,
  onCancelClicked,
  // Markdown imports only: the chapter-split mode the document was converted
  // with, and a callback to re-convert it with another one. Absent for
  // DOCX/PDF, where the split is decided by the converter and cannot be redone
  // without re-uploading.
  splitMode,
  onSplitModeChange,
  splitModeBusy
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [cutPosition, setCutPosition] = useState();
  const maxSupportedCharacterCount = 200000;
  const {t} = useTranslation();
  const {documentData, setDocumentData} = usePostProcessingContext();

  useEffect(() => {
    ux.trackModalView('upload-post-processor');
    setTimeout(function () {
      // slide sidebar closed to show that it's there
      setSidebarOpen(false);
    }, 500);
  }, []);

  const removePart = event => {
    ux.trackEvent({
      category: 'Imports',
      action: 'clicked-remove-part'
    });
    const {part} = event.currentTarget.dataset;
    const index = parseInt(part, 10);
    const updatedParts = [...documentData.parts];
    // remove part
    updatedParts.splice(index, 1);
    // update parent

    setDocumentData({
      ...documentData,
      maxPartWordCount: updatedParts.reduce(
        (total, comparePart) => Math.max(total, comparePart.wordCount),
        0
      ),
      maxPartCharCount: updatedParts.reduce(
        (total, comparePart) => Math.max(total, comparePart.characterCount),
        0
      ),
      parts: updatedParts
    });
  };

  const showPart = event => {
    ux.trackEvent({
      category: 'Imports',
      action: 'clicked-show-part'
    });
    const {part} = event.currentTarget.dataset;
    const partElement = document.getElementById(`part_${part}`);
    if (partElement) {
      partElement.scrollIntoView({
        behavior: 'auto',
        block: 'start'
      });
    }
  };

  const onDocumentTitleChanged = event => {
    setDocumentData({
      ...documentData,
      title: event.target.value
    });
  };

  const onPartTitleChanged = event => {
    const index = Number(event.currentTarget.dataset.part);
    const updatedParts = [...documentData.parts];
    // update part
    const updatedPart = {
      ...documentData.parts[index],
      title: event.target.value
    };
    updatedParts.splice(index, 1, updatedPart);

    // update parent
    setDocumentData({
      ...documentData,
      parts: updatedParts
    });
  };

  const splitPart = async event => {
    ux.trackEvent({
      category: 'Imports',
      action: 'clicked-split-part'
    });
    // find split position
    const {dataset} = event.currentTarget;
    const partIndex = parseInt(dataset.partIndex, 10);

    // The split must be computed over TOP-LEVEL BLOCKS, not over paragraphs.
    // This used to collect `querySelectorAll('p')` and rebuild both halves out
    // of those elements alone, so every non-paragraph block — images, headings,
    // lists, blockquotes, tables, horizontal rules — was never collected and
    // therefore never written back: one click on "split here" deleted all of it
    // from BOTH halves. It also hoisted nested paragraphs out of the block that
    // contained them, turning a blockquote into a bare paragraph.
    //
    // EPUB import is where this became visible: the importer deliberately lifts
    // images out of paragraphs into standalone top-level <img> blocks (the
    // tiptap Image node is a block node), so an imported illustration is always
    // a non-paragraph block — i.e. always destroyed by a split.
    const blocks = Array.from(event.currentTarget.children);

    // Paragraphs are the split affordance (only they get the dashed hover
    // underline), so a click outside one is not a split request. `closest`
    // rather than an identity check, so clicking inline markup inside a
    // paragraph — a <strong>, an <a> — still counts as clicking the paragraph.
    const clickedParagraph =
      event.target && event.target.closest && event.target.closest('p');
    if (!clickedParagraph || !event.currentTarget.contains(clickedParagraph)) {
      return;
    }

    // Walk up from the clicked paragraph to the block that owns it: clicking a
    // paragraph inside a blockquote or a list item splits after the whole
    // blockquote/list, never through the middle of it.
    let clickedBlock = clickedParagraph;
    while (clickedBlock && clickedBlock.parentElement !== event.currentTarget) {
      clickedBlock = clickedBlock.parentElement;
    }
    const clickedIndex = blocks.indexOf(clickedBlock);
    if (clickedIndex === -1 || clickedIndex === blocks.length - 1) {
      // nothing would move down: not a split
      return;
    }

    // split part into two; above and below split position
    const aboveSplit = blocks.slice(0, clickedIndex + 1);
    const belowSplit = blocks.slice(clickedIndex + 1);

    // update parent with the new parts list
    const updatedParts = [...documentData.parts];
    // remove part and replace with splits
    const aboveSplitHtml = aboveSplit.reduce((string, element) => {
      const newString = string + element.outerHTML;
      return newString;
    }, '');
    const aboveSplitWordCount = await countWords.WordCount(aboveSplitHtml);

    const aboveSplitPartData = {
      // the old part keeps its title
      title: documentData.parts[partIndex].title,
      html: aboveSplitHtml,
      wordCount: aboveSplitWordCount.WordCount,
      characterCount: aboveSplitWordCount.CharacterCount
    };

    const belowSplitHtml = belowSplit.reduce((string, element) => {
      const newString = string + element.outerHTML;
      return newString;
    }, '');
    const belowSplitWordCount = await countWords.WordCount(belowSplitHtml);

    const belowSplitPartData = {
      // the old part keeps its title
      title: documentData.parts[partIndex].title,
      html: belowSplitHtml,
      wordCount: belowSplitWordCount.WordCount,
      characterCount: belowSplitWordCount.CharacterCount
    };

    updatedParts.splice(partIndex, 1, aboveSplitPartData, belowSplitPartData);
    // update parent
    setDocumentData({
      ...documentData,
      maxPartWordCount: updatedParts.reduce(
        (total, part) => Math.max(total, part.wordCount),
        0
      ),
      maxPartCharCount: updatedParts.reduce(
        (total, part) => Math.max(total, part.characterCount),
        0
      ),
      parts: updatedParts
    });
  };

  const stitchParts = async event => {
    ux.trackEvent({
      category: 'Imports',
      action: 'clicked-stitch-parts'
    });
    // find stitch position
    const {dataset} = event.currentTarget;
    const partIndex = parseInt(dataset.partIndex, 10);

    // join current part with previous part
    const currentPart = documentData.parts[partIndex];
    const previousPart = documentData.parts[partIndex - 1];

    if (currentPart && previousPart) {
      // The merged-away part's title is real manuscript text: for a Markdown
      // import the heading line is consumed into `part.title` and stripped from
      // the body, so simply dropping it (this used to keep only
      // `previousPart`'s fields) made the heading vanish from the chapter.
      // Re-inject it as a heading at the seam instead.
      const carriedTitle = (currentPart.title || '').trim();
      const carriedTitleHtml = carriedTitle
        ? `<h2>${carriedTitle.replace(
            /[&<>]/g,
            c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;'}[c])
          )}</h2>`
        : '';
      // Build a new string rather than `previousPart.html += …`, which mutated
      // the part object shared with `originalDocumentData` (so a later reset
      // restored already-merged HTML).
      const newPartHtml = `${previousPart.html}${carriedTitleHtml}${currentPart.html}`;

      // count words and charachters
      const wordCountResult = await countWords.WordCount(newPartHtml);

      const newPart = {
        ...previousPart,
        html: newPartHtml,
        wordCount: wordCountResult.WordCount,
        characterCount: wordCountResult.CharacterCount
      };

      // update parent with the new parts list
      const updatedParts = [...documentData.parts];
      // replace both old parts with the new part
      updatedParts.splice(partIndex - 1, 2, newPart);
      // update parent
      setDocumentData({
        ...documentData,
        maxPartWordCount: Math.max(
          documentData.maxPartWordCount,
          wordCountResult.WordCount
        ),
        maxPartCharCount: Math.max(
          documentData.maxPartCharCount,
          wordCountResult.CharacterCount
        ),
        parts: updatedParts
      });
    }
  };

  const hoverPart = event => {
    // find the current paragraph
    const paragraphs = [].slice.call(
      event.currentTarget.querySelectorAll('p'),
      0
    );
    const currentParagraphIndex = paragraphs.indexOf(event.target);
    // set cut position if we're not on the last paragraph of a part
    if (
      currentParagraphIndex >= 0 &&
      currentParagraphIndex < paragraphs.length - 1
    ) {
      const rect = event.target.getBoundingClientRect();
      setCutPosition({
        top: rect.top + rect.height - 10,
        left: rect.right + 20
      });
    } else {
      setCutPosition(undefined);
    }
  };

  return (
    <PreviewOverlay>
      {cutPosition && (
        <Icon
          name='cut'
          size='large'
          style={{
            position: 'absolute',
            top: cutPosition.top,
            left: cutPosition.left
          }}
        />
      )}
      <PreviewSidebar open={sidebarOpen}>
        <SidebarToggle
          link
          size='large'
          name={sidebarOpen ? 'chevron circle left' : 'chevron circle right'}
          aria-label={sidebarOpen ? 'Close chapter sidebar' : 'Open chapter sidebar'}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        />
        {documentData && documentData.parts && (
          <>
            <ul style={{listStyle: 'none', padding: 0}}>
              {onSplitModeChange && (
                <li>
                    <label htmlFor="chapter-split-mode">{t('ChapterSplit')}</label>
                    <select
                      id="chapter-split-mode"
                      value={splitMode || 'auto'}
                      disabled={splitModeBusy}
                      onChange={event => onSplitModeChange(event.target.value)}
                    >
                      <option value="auto">{t('ChapterSplitAuto')}</option>
                      <option value="h1">{t('ChapterSplitH1')}</option>
                      <option value="h2">{t('ChapterSplitH2')}</option>
                      <option value="h3">{t('ChapterSplitH3')}</option>
                      <option value="none">{t('ChapterSplitNone')}</option>
                    </select>
                </li>
              )}
              <li>
                    <ChapterParser documentData={documentData} />
              </li>
              {documentData.parts.map((part, index) => (
                <li key={index}>
                  <button
                    type="button"
                    data-part={index}
                    onClick={showPart}
                    title={part.characterCount > maxSupportedCharacterCount
                      ? `${t('ExceedChapterLenghtWarning')} ${t('CharCountString', {count: part.characterCount})} / ${maxSupportedCharacterCount}`
                      : undefined}
                  >
                    {part.characterCount > maxSupportedCharacterCount && (
                      <Icon color='orange' name='exclamation triangle' />
                    )}
                    {part.title || `${t('Chapter')} ${index + 1}`}{' '}
                    ({t('WordCountString', {count: part.wordCount})})
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </PreviewSidebar>
      <PreviewContent>
        <PreviewActions>
          <Button variant="secondary" onClick={onCancelClicked}>{t('Cancel')}</Button>
          <Button
            disabled={
              !documentData ||
              documentData.parts.length === 0 ||
              documentData.maxPartCharCount > maxSupportedCharacterCount
            }
            onClick={onCompleteClicked}
          >{t('CompleteImport')}</Button>
        </PreviewActions>
        <header>
          <label htmlFor="import-document-title">{t('Title')}</label>
          <TitleInput
            id="import-document-title"
            placeholder={documentData.title}
            value={documentData.title}
            onChange={onDocumentTitleChanged}
          />
          <p>{t('ChapterCount', {count: documentData.parts.length})}</p>
        </header>

        {documentData.maxPartCharCount > maxSupportedCharacterCount && (
          <Alert variant="warning">
            <strong>{t('LongChapterWarning')}</strong>
            <Trans i18nKey='ImportExeedLenghMessage'>
              <div style={{fontSize: '1em'}}>
                <p>
                  One or more of your chapters appear to be too long to import,
                  make sure you have have followed our
                  <a
                    href='https://betareader.io/help/betareader-format-specification'
                    target='_blank'
                    rel='noopener noreferrer'>
                    {' '}
                    import guidelines.{' '}
                  </a>
                </p>
                <p>
                  Tip: you can hower over the chapter text and use the sizzor
                  tool to split larger chapters
                </p>
              </div>
            </Trans>
          </Alert>
        )}
        <div>
          {documentData.parts.map((part, index, array) => (
            <div key={index} style={{marginTop: '2rem'}} id={`part_${index}`}>
              {index > 0 && index < array.length && (
                <div style={{textAlign: 'center'}}>
                  <Icon
                    size='large'
                    name='attach'
                    aria-label={t('MergeChapters')}
                    data-part-index={index}
                    onClick={stitchParts}
                  />
                </div>
              )}
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <label htmlFor={`import-part-title-${index}`}>{t('Chapter')} {index + 1}</label>
                <TitleInput
                  id={`import-part-title-${index}`}
                  placeholder={`${t('Chapter')} ${index + 1}`}
                  value={part.title || ''}
                  data-part={index}
                  onChange={onPartTitleChanged}
                />
                <Icon
                  data-part={index}
                  name='trash alternate'
                  aria-label={`${t('Delete')} ${t('Chapter')} ${index + 1}`}
                  onClick={removePart}
                />
              </div>
              <div>
                <p style={{color: 'grey'}}>
                  {part.characterCount > maxSupportedCharacterCount && (
                    <Icon color='orange' name='exclamation triangle' />
                  )}
                  {t('WordCountString', {count: part.wordCount})}
                </p>
              </div>
              <ContentBlock
                className='post-processor-part'
                data-part-index={index}
                onClick={splitPart}
                onMouseOver={hoverPart}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(transformCalloutsForPreview(part.html))
                }}
              />
            </div>
          ))}
        </div>
      </PreviewContent>
    </PreviewOverlay>
  );
};

export default ChapterFilePostProcessor;
